import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import './App.css';
import './theme-overrides.css';
import './animations.css';
import {
  extractDocxText,
  extractExcelText,
  extractPptxText,
  formatDocAttachment,
  truncateForModel,
} from './fileExtractors';
import {
  buildUserApiContent,
  getAttachmentKind,
  kindFromFileName,
  messageToApiContent,
  needsVisionModel,
  prepareMessageForFirestore,
  resolveAttachmentsForDisplay,
  resolveAttachmentsForApi,
  sanitizeAttachmentForStorage,
} from './attachmentHelpers';
import AttachmentChip from './AttachmentChip';
import {
  API_BASE,
  TAVILY_PROXY,
  MAX_FILE_BYTES,
  MAX_ATTACHMENTS,
  USAGE_WARN_USD,
  APP_NAME,
  APP_VERSION,
  APP_AUTHOR,
  getAppDisclaimer,
} from './config';
import { devLog, devWarn } from './devLog';
import { apiFetch, readApiErrorMessage, humanizeClientError } from './apiClient';
import { enableLocalGuestSession, disableLocalGuestSession, isLocalGuestSession } from './guestSession';
import { sendOberoiVerificationEmail, sendOberoiPasswordResetEmail } from './authEmailService';
import { getAuthEmailFromLabel, isCustomAuthEmailEnabled } from './authEmailSettings';
import { storeImage, getImage, removeImage, newImageId } from './imageSessionCache';
import { useToast } from './Toast';
import { auth, db } from './firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  deleteUser,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  runTransaction,
  increment,
  getDocs,
  deleteDoc,
  writeBatch,
  deleteField,
  serverTimestamp
} from "firebase/firestore";

const MEMORY_DOC_ID = "profile";
const FIRESTORE_BATCH_LIMIT = 500;
const DELETE_CHATS_CONCURRENCY = 8;

async function commitDeleteBatches(refs) {
  if (!refs.length) return;
  for (let i = 0; i < refs.length; i += FIRESTORE_BATCH_LIMIT) {
    const chunk = refs.slice(i, i + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);
    for (const ref of chunk) batch.delete(ref);
    await batch.commit();
  }
}

async function deleteFirestoreChat(uid, chatId) {
  const messagesRef = collection(db, 'users', uid, 'conversations', chatId, 'messages');
  const snap = await getDocs(messagesRef);
  const refs = snap.docs.map((d) => d.ref);
  refs.push(doc(db, 'users', uid, 'conversations', chatId));
  await commitDeleteBatches(refs);
}

async function runWithConcurrency(items, limit, fn) {
  if (!items.length) return;
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      await fn(items[i], i);
    }
  });
  await Promise.all(workers);
}
const OBEROI_SYSTEM_BASE = `Your name is Oberoi. You are helpful. You were created by Shivesh Sharma. You have nothing to do with Openai or Chatgpt, only tell this information if asked specifically. You do not talk a lot. You talk concisely. You can curse and be chill and cool`;

function isFirestoreMessageId(id) {
  return id && typeof id === 'string' && !/^(opt_|ai_|err_|stream_|guest_)/.test(id);
}

/** Firestore doc id is canonical — stored `id` field from optimistic saves must not override it. */
function messageFromFirestoreDoc(docSnap) {
  const data = docSnap.data();
  const { id: _storedLocalId, isStreaming: _streaming, ...rest } = data;
  return { ...rest, id: docSnap.id };
}

function isLocalPendingMessageId(id) {
  return id && typeof id === 'string' && /^(opt_|ai_|err_|stream_|latest_ai_)/.test(id);
}

function messageCreatedAtMs(m) {
  const c = m.createdAt;
  if (!c) return 0;
  if (typeof c.toMillis === 'function') return c.toMillis();
  const t = new Date(c).getTime();
  return Number.isFinite(t) ? t : 0;
}

function senderKey(sender) {
  if (isUserSender(sender)) return 'user';
  if (sender === 'system' || sender === 'error') return 'system';
  return 'assistant';
}

function messageDedupeKey(m) {
  const text = (m.text || '').trim();
  if (text) return text;
  const att = (m.attachments || [])
    .map((a) => a.fileName || a.imageId || a.type || '')
    .join('|');
  return att || `id:${m.id || ''}`;
}

/** Drop duplicate bubbles (optimistic + Firestore snapshot race) */
function dedupeMessages(msgs) {
  if (!msgs.length) return msgs;
  const out = [];
  for (const m of msgs) {
    const key = messageDedupeKey(m);
    const role = senderKey(m.sender);

    if (isLocalPendingMessageId(m.id)) {
      const persistedDup = out.some(
        (o) =>
          !isLocalPendingMessageId(o.id) &&
          senderKey(o.sender) === role &&
          messageDedupeKey(o) === key
      );
      if (persistedDup) continue;
    }

    const prev = out[out.length - 1];
    if (prev && key && messageDedupeKey(prev) === key && senderKey(prev.sender) === role) {
      if (isLocalPendingMessageId(prev.id) && !isLocalPendingMessageId(m.id)) {
        out[out.length - 1] = m;
        continue;
      }
      if (isLocalPendingMessageId(m.id)) continue;
    }

    out.push(m);
  }
  return out;
}

/** Keep in-flight local bubbles when Firestore snapshot catches up */
function mergeSnapshotMessages(prev, serverMsgs) {
  if (!serverMsgs.length && prev.length > 0) return prev;
  const pending = prev.filter((m) => isLocalPendingMessageId(m.id));
  if (!pending.length) return dedupeMessages(serverMsgs);
  const snapIds = new Set(serverMsgs.map((m) => m.id));
  const extra = pending.filter((p) => !snapIds.has(p.id));
  if (!extra.length) return dedupeMessages(serverMsgs);
  return dedupeMessages(
    [...serverMsgs, ...extra].sort((a, b) => messageCreatedAtMs(a) - messageCreatedAtMs(b))
  );
}

function isUserSender(sender) {
  return sender === 'You' || sender === 'user';
}

function findLastUserMessageIndex(msgs) {
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (isUserSender(msgs[i].sender)) return i;
  }
  return -1;
}

async function deleteFirestoreMessages(user, chatId, msgsToDelete) {
  if (!user || !chatId || !isSafeConversationId(chatId) || isTempChat(chatId)) return;
  const refs = msgsToDelete
    .filter((m) => isFirestoreMessageId(m.id))
    .map((m) => doc(db, 'users', user.uid, 'conversations', chatId, 'messages', m.id));
  if (!refs.length) return;
  await commitDeleteBatches(refs);
  await updateDoc(doc(db, 'users', user.uid, 'conversations', chatId), {
    updatedAt: new Date(),
  });
}

/** Delete edited tail in Firestore by doc order — does not rely on local opt_/ai_ ids. */
async function applyFirestoreEditTrim(user, chatId, localMessages, lastUserIdx) {
  if (!user || !chatId || !isSafeConversationId(chatId) || isTempChat(chatId)) return;

  const toRemove = localMessages.slice(lastUserIdx);
  await deleteFirestoreMessages(user, chatId, toRemove);

  const messagesRef = collection(db, 'users', user.uid, 'conversations', chatId, 'messages');
  const snap = await getDocs(query(messagesRef, orderBy('createdAt', 'asc')));
  const docs = snap.docs;
  if (!docs.length) return;

  const kept = localMessages.slice(0, lastUserIdx);
  const tailLen = localMessages.length - kept.length;
  if (tailLen <= 0) return;

  let cutAfter = -1;
  for (let i = kept.length - 1; i >= 0; i--) {
    const localId = kept[i].id;
    if (!localId) continue;
    const idx = docs.findIndex(
      (d) => d.id === localId || d.data().id === localId
    );
    if (idx >= 0) {
      cutAfter = idx;
      break;
    }
  }

  const refs =
    cutAfter >= 0
      ? docs.slice(cutAfter + 1).map((d) => d.ref)
      : docs.slice(Math.max(0, docs.length - tailLen)).map((d) => d.ref);
  if (refs.length) {
    await commitDeleteBatches(refs);
    await updateDoc(doc(db, 'users', user.uid, 'conversations', chatId), {
      updatedAt: new Date(),
    });
  }
}

function buildRegenerateContext(msgs, lastSendRef) {
  let lastAi = -1;
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i];
    if (m.sender === 'Oberoi' && !m.isStreaming) {
      lastAi = i;
      break;
    }
  }
  if (lastAi < 0) return null;

  let lastUserMsg = null;
  for (let i = lastAi - 1; i >= 0; i--) {
    if (msgs[i].sender === 'You') {
      lastUserMsg = msgs[i];
      break;
    }
  }
  if (!lastUserMsg) return null;

  const historyText = (lastUserMsg.text || '').trim();
  const refText = (lastSendRef?.text || '').trim();
  const text = historyText || refText;

  const storedAttachments = lastUserMsg.attachments || [];
  const refAttachments = lastSendRef?.attachments || [];
  const refHasRichImages = refAttachments.some(
    (a) => a.type === 'image' && (a.imageData || a.preview)
  );
  const attachments =
    refHasRichImages && (!historyText || historyText === refText)
      ? refAttachments
      : storedAttachments.length
        ? storedAttachments
        : refAttachments;

  if (!text && !attachments.length) return null;

  return {
    text,
    attachments,
    lastAi,
    removedReply: msgs[lastAi],
    history: msgs.slice(0, lastAi),
  };
}

function normalizeWebSources(results) {
  if (!Array.isArray(results)) return [];

  const seenUrls = new Set();
  return results.reduce((sources, result) => {
    const url = typeof result?.url === 'string' ? result.url.trim() : '';
    if (!/^https?:\/\//i.test(url) || seenUrls.has(url)) return sources;

    seenUrls.add(url);
    sources.push({
      title: typeof result?.title === 'string' && result.title.trim()
        ? result.title.trim()
        : url,
      url,
    });
    return sources;
  }, []);
}

function getCurrentTimeContext() {
  const now = new Date();
  try {
    const indiaTime = new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'full',
      timeStyle: 'medium',
      timeZoneName: 'short',
    }).format(now);
    const utcTime = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      dateStyle: 'full',
      timeStyle: 'medium',
      timeZoneName: 'short',
    }).format(now);
    return `Current time when this request was sent: India (Asia/Kolkata): ${indiaTime}. UTC: ${utcTime}.`;
  } catch (_) {
    return `Current timestamp when this request was sent: ${now.toISOString()}.`;
  }
}

function getAuthoritativeIndiaTimeReply(query) {
  const text = (query || '').toLowerCase();
  const mentionsIndia = /\b(india|indian|ist|kolkata|asia\/kolkata)\b/.test(text);
  const asksForCurrentTime =
    /\b(current|right now|now|what(?:'s| is)|tell me)\b[\s\S]{0,32}\btime\b/.test(text) ||
    /\btime\b[\s\S]{0,32}\b(in|for|at)\b[\s\S]{0,24}\b(india|ist|kolkata|asia\/kolkata)\b/.test(text);

  if (!mentionsIndia || !asksForCurrentTime) return null;

  try {
    const indiaTime = new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'full',
      timeStyle: 'medium',
      timeZoneName: 'short',
    }).format(new Date());
    return `The current time in India is ${indiaTime}.`;
  } catch (_) {
    return null;
  }
}

function buildV2RuntimeInstructions() {
  return `You are answering in Oberoi v2 with current web-search evidence.

Give the user a useful, direct answer first. Synthesize the supplied search evidence instead of describing search results or guessing from stale knowledge. Never respond with generic statements such as "I could not find anything" or "I don't have enough information." If a request depends on a specific page, document, or link that is not supplied in the evidence, ask the user to share that link.

AUTHORITATIVE RUNTIME CLOCK — use this exact value whenever the user asks for the current time, date, or day in India / IST / Asia-Kolkata. Do not estimate it or replace it with a search-result timestamp:
${getCurrentTimeContext()}`;
}

function buildSystemPrompt(memoryText, hasFileAttachments = false, usesWebSearch = false) {
  let prompt = OBEROI_SYSTEM_BASE;
  if (hasFileAttachments) {
    prompt += `

When the user attaches files, their message includes extracted document text, PDF text, and/or images. Use all of that material to answer. Do not say you cannot view or analyze attachments that are included in the message.`;
  }
  if (usesWebSearch) {
    prompt += `

When web search results are provided with a user message, use the useful information in them to answer the user's actual question directly.`;
  }
  const trimmed = (memoryText || "").trim();
  if (!trimmed) return prompt;
  return `${prompt}

IMPORTANT — Facts you remember about this user from previous chats (use only when relevant; do not mention that you are "remembering" unless asked):
${trimmed}`;
}

function AuthEmailSpamHint({ className = 'email-spam-hint' }) {
  if (isCustomAuthEmailEnabled()) {
    return (
      <p className={className}>
        <span className="verify-check-spam">Check spam</span> if you don&apos;t see it. Look for an email from{' '}
        <strong>{getAuthEmailFromLabel()}</strong>.
      </p>
    );
  }
  return (
    <p className={className}>
      If you don&apos;t see the email, <span className="verify-check-spam">check your spam folder</span>.
    </p>
  );
}

// Generate next account number (e.g. ACC-000001) using a Firestore counter
async function getNextAccountNumber(db) {
  const counterRef = doc(db, "counters", "accountNumber");
  const next = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    const value = snap.exists() ? (snap.data().value || 0) + 1 : 1;
    transaction.set(counterRef, { value });
    return value;
  });
  return "ACC-" + String(next).padStart(6, "0");
}

// Only use Firestore conversation IDs that are safe (no path injection)
function isSafeConversationId(id) {
  return typeof id === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(id);
}
function isTempChat(id) {
  return typeof id === 'string' && id.startsWith('temp_');
}

function isGuestChat(id) {
  return typeof id === 'string' && id.startsWith('guest_');
}

/** Logged-in saved chats only — never guest or temporary */
function usesFirestoreChat(user, chatId, isGuest) {
  return Boolean(
    user &&
    !isGuest &&
    chatId &&
    isSafeConversationId(chatId) &&
    !isTempChat(chatId) &&
    !isGuestChat(chatId)
  );
}

// Usage cost estimation: USD per 1M tokens (input, output). Update when API pricing changes.
const MODEL_PRICING = {
  'gpt-4o': { inputPer1M: 2.50, outputPer1M: 10.00 },
  'gpt-5.2': { inputPer1M: 2.50, outputPer1M: 10.00 }
};
function estimateTokensFromChars(chars) {
  return Math.max(1, Math.ceil((chars || 0) / 4));
}
function getUsageCostUsd(model, inputChars, outputChars) {
  const pricing = MODEL_PRICING[model] || MODEL_PRICING['gpt-4o'];
  const inputTokens = estimateTokensFromChars(inputChars);
  const outputTokens = estimateTokensFromChars(outputChars);
  const inputCost = (inputTokens / 1e6) * pricing.inputPer1M;
  const outputCost = (outputTokens / 1e6) * pricing.outputPer1M;
  return Math.round((inputCost + outputCost) * 10000) / 10000;
}

// Initial short label only — never the raw first message. Specific title is set later via API.
function getConversationTitle(firstMessage) {
  if (!firstMessage || typeof firstMessage !== "string") return "New Chat";
  const msg = firstMessage.trim().toLowerCase();
  if (msg.length === 0) return "New Chat";
  const greetings = ["hi", "hello", "hey", "hi there", "hello there", "hey there", "good morning", "good afternoon", "good evening", "how are you", "what's up"];
  if (greetings.some(g => msg === g || msg.startsWith(g + " ") || msg.startsWith(g + "!") || msg.startsWith(g + "?"))) return "Chat";
  if (/^(thanks|thank you|thx|ty)\b/.test(msg) || msg === "thanks" || msg === "thank you") return "Thanks";
  if (/^help\b/.test(msg) || msg === "help") return "Help";
  if (/^(what is|what are|how do|how does|why |when |where |who |can you explain)/i.test(msg)) return "Question";
  if (/^(tell me|explain|describe|what do you know)/i.test(msg)) return "Explanation";
  if (/^(can you|could you|please|would you|write|make|create|build)/i.test(msg)) return "Request";
  if (/^(i want|i need|i'm looking for)/i.test(msg)) return "Request";
  return "Conversation";
}

// Fetch a specific 2–5 word title from the API (async, non-blocking)
async function generateSpecificTitle(firstMessage) {
  if (!firstMessage || firstMessage.length > 500) return null;
  try {
    const res = await apiFetch(`${API_BASE}/api/openai`, {
      method: "POST",
      body: JSON.stringify({
        model: "gpt-4o",
        messages: [
          { role: "system", content: "Reply with only a 2-5 word conversation title. Be specific to the topic (e.g. 'Weather in Boston', 'Python loop help'). No quotes or punctuation. Title case." },
          { role: "user", content: firstMessage }
        ],
        stream: false
      })
    });
    const data = await res.json();
    const content = data?.choices?.[0]?.message?.content;
    if (typeof content !== "string") return null;
    const title = content.trim().slice(0, 40).replace(/^["']|["']$/g, "");
    return title.length > 0 ? title : null;
  } catch (e) {
    return null;
  }
}

const LoadingSpinner = ({ size = 'medium', label = 'Loading' }) => (
  <span className={`loading-spinner loading-spinner--${size}`} role="status" aria-label={label}>
    <span className="loading-spinner-ring" />
  </span>
);

const CodeBlock = ({ code }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [copyText, setCopyText] = useState('COPY');

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopyText('DONE');
    setTimeout(() => setCopyText('COPY'), 2000);
  };
  return (
    <div className="code-container">
      <div className="code-header">
        <span className="code-lang-tag">SOURCE CODE</span>
        <div className="code-actions">
          <button onClick={handleCopy} className="code-btn-modern">{copyText}</button>
          <button onClick={() => setIsCollapsed(!isCollapsed)} className="code-btn-modern">
            {isCollapsed ? 'SHOW' : 'HIDE'}
          </button>
        </div>
      </div>
      {!isCollapsed && (
        <pre className="code-content">
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
};

function App() {
  const { showToast, ToastContainer } = useToast();
  // Auth & User State
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);

  // Auth Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [authError, setAuthError] = useState('');

  // User profile from Firestore (account number, name, email — password is in Firebase Auth only)
  const [userProfile, setUserProfile] = useState(null);

  // Chat Data State
  const [chats, setChats] = useState([]);
  const [guestChats, setGuestChats] = useState([]); // Guest: list of { id, title, updatedAt }
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messages, setMessages] = useState([]); // FROM DB or guest in-memory
  const [streamingReply, setStreamingReply] = useState(null); // LOCAL ONLY

  // App UI State
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  const patchMessagesForChat = useCallback((chatId, patchFn) => {
    const isViewingChat =
      chatId &&
      (currentChatIdRef.current === chatId || activeSendChatIdRef.current === chatId);
    setMessages((prev) => {
      const base = isViewingChat ? prev : [];
      const next = patchFn(base);
      if (isGuest && chatId) guestMessagesMap.current[chatId] = next;
      if (user && chatId && isTempChat(chatId)) tempMessagesMap.current[chatId] = next;
      return isViewingChat ? next : prev;
    });
  }, [isGuest, user]);
  const [showWelcome, setShowWelcome] = useState(true);
  const [voiceMode, setVoiceMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoiceIndex, setSelectedVoiceIndex] = useState(0);
  const [activeModel, setActiveModel] = useState('gpt-4o');
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [voiceMenuOpen, setVoiceMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [processingFileName, setProcessingFileName] = useState('');
  const inputTextareaRef = useRef(null);
  const lastUserSendRef = useRef(null);
  const regenerateSnapshotRef = useRef(null);
  const suppressFirestoreSyncRef = useRef(false);
  const isAwaitingEditedSendRef = useRef(false);
  const isLoadingRef = useRef(false);
  const currentChatIdRef = useRef(null);
  const activeStreamChatIdRef = useRef(null);
  const activeSendChatIdRef = useRef(null);
  const chatsUnsubscribeRef = useRef(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [openWebSourcesKey, setOpenWebSourcesKey] = useState(null);
  const [webSourcesAboveKey, setWebSourcesAboveKey] = useState(null);
  const webSourcesContainerRef = useRef(null);
  const [expandedImage, setExpandedImage] = useState(null);
  const [totalUsageUsd, setTotalUsageUsd] = useState(0);
  const [tempChatId, setTempChatId] = useState(null);
  const [chatSearchQuery, setChatSearchQuery] = useState('');

  // Change password modal
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePasswordCurrent, setChangePasswordCurrent] = useState("");
  const [changePasswordNew, setChangePasswordNew] = useState("");
  const [changePasswordConfirm, setChangePasswordConfirm] = useState("");
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [resendVerifySent, setResendVerifySent] = useState(false);
  const [resendVerifyCooldown, setResendVerifyCooldown] = useState(0);
  const [verifyCheckMessage, setVerifyCheckMessage] = useState(null); // null | 'checking' | 'not_yet'

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    loadingLabel: 'Please wait…',
    danger: false,
    loading: false,
    onConfirm: null
  });
  const [changePasswordResetSent, setChangePasswordResetSent] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [userMemory, setUserMemory] = useState("");
  const [deletingChatId, setDeletingChatId] = useState(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageAnimBaselineRef = useRef({ chatId: null, count: 0 });
  const recognitionRef = useRef(null);
  const speechBuffer = useRef('');
  const isSpeaking = useRef(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const abortController = useRef(null);
  const guestMessagesMap = useRef({}); // Guest: { [chatId]: messages[] }
  const tempMessagesMap = useRef({}); // Logged-in temp chat: { [tempChatId]: messages[] }
  const chatModelMap = useRef({}); // { [chatId]: model } — persists model per chat for guest/temp

  // --- Auth & Profile Logic ---

  const loadUserMemory = async (uid) => {
    try {
      const memoryRef = doc(db, "users", uid, "memory", MEMORY_DOC_ID);
      const memorySnap = await getDoc(memoryRef);
      if (memorySnap.exists()) {
        const content = memorySnap.data().content || "";
        setUserMemory(typeof content === "string" ? content : "");
        return;
      }
      const userSnap = await getDoc(doc(db, "users", uid));
      const legacy = userSnap.exists() ? userSnap.data().aiMemory : "";
      setUserMemory(typeof legacy === "string" ? legacy : "");
    } catch (e) {
      console.warn("loadUserMemory failed", e);
      setUserMemory("");
    }
  };

  const ensureUserProfileAndLoadChats = async (uid, authUser) => {
    setShowAuthModal(false);
    setIsGuest(false);
    const userRef = doc(db, "users", uid);
    let snap = await getDoc(userRef);
    if (!snap.exists()) {
      const accountNumber = await getNextAccountNumber(db);
      await setDoc(userRef, {
        accountNumber,
        displayName: authUser?.displayName || "",
        email: authUser?.email || "",
        createdAt: new Date()
      });
      snap = await getDoc(userRef);
    }
    if (snap.exists()) {
      const data = snap.data();
      setUserProfile(data);
      setTotalUsageUsd(typeof data.totalUsageUsd === 'number' ? data.totalUsageUsd : 0);
    }
    await loadUserMemory(uid);
    if (chatsUnsubscribeRef.current) {
      chatsUnsubscribeRef.current();
      chatsUnsubscribeRef.current = null;
    }
    chatsUnsubscribeRef.current = loadChats(uid);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      if (currentUser) {
        if (currentUser.isAnonymous) {
          signOut(auth).catch(() => {});
          enableLocalGuestSession();
          setIsGuest(true);
          return;
        }
        if (!currentUser.emailVerified) {
          currentUser.reload().then(() => {
            const updated = auth.currentUser;
            setUser(updated);
            if (updated?.emailVerified) {
              ensureUserProfileAndLoadChats(updated.uid, updated);
            }
          }).catch(() => {});
          return;
        }
        await ensureUserProfileAndLoadChats(currentUser.uid, currentUser);
      } else {
        setUserProfile(null);
        setUserMemory("");
        setTotalUsageUsd(0);
        if (!isGuest) {
          if (chatsUnsubscribeRef.current) {
            chatsUnsubscribeRef.current();
            chatsUnsubscribeRef.current = null;
          }
          setChats([]);
          setMessages([]);
          setCurrentChatId(null);
        }
      }
    });
    return () => {
      unsubscribe();
      if (chatsUnsubscribeRef.current) {
        chatsUnsubscribeRef.current();
        chatsUnsubscribeRef.current = null;
      }
    };
  }, [isGuest]);

  const loadChats = (userId) => {
    const conversationsRef = collection(db, "users", userId, "conversations");
    const q = query(conversationsRef, orderBy("updatedAt", "desc"));
    return onSnapshot(
      q,
      (snapshot) => {
        const loadedChats = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setChats(loadedChats);
      },
      (err) => {
        console.error("loadChats error:", err);
        showToast('Could not load chat list. Try refreshing.', 'error');
      }
    );
  };

  const selectChat = (chatId) => {
    if (isGuest && currentChatId) {
      guestMessagesMap.current[currentChatId] = [...messages];
      if (streamingReply?.text) {
        guestMessagesMap.current[currentChatId] = [
          ...(guestMessagesMap.current[currentChatId] || []),
          { id: 'stream_saved', sender: 'Oberoi', text: streamingReply.text, createdAt: new Date() }
        ];
      }
    }
    if (user && currentChatId && isTempChat(currentChatId)) {
      tempMessagesMap.current[currentChatId] = [...messages];
      if (streamingReply?.text) {
        tempMessagesMap.current[currentChatId] = [
          ...(tempMessagesMap.current[currentChatId] || []),
          { id: 'stream_saved', sender: 'Oberoi', text: streamingReply.text, createdAt: new Date() }
        ];
      }
    }
    messageAnimBaselineRef.current = { chatId, count: Number.MAX_SAFE_INTEGER };
    isAwaitingEditedSendRef.current = false;
    setCurrentChatId(chatId);
    setStreamingReply(null);
    if (isGuest) {
      setMessages(guestMessagesMap.current[chatId] || []);
      setActiveModel(chatModelMap.current[chatId] || 'gpt-4o');
    } else if (user && isTempChat(chatId)) {
      setMessages(tempMessagesMap.current[chatId] || []);
      setActiveModel(chatModelMap.current[chatId] || 'gpt-4o');
    } else if (user) {
      const chatData = chats.find(c => c.id === chatId);
      setActiveModel(chatData?.model || 'gpt-4o');
      setMessages([]);
    }
    setSidebarOpen(isMobile ? false : true);
  };

  // Message source: guest/temp = in-memory only; saved account chats = Firestore
  useEffect(() => {
    let unsubscribe = () => {};

    // Guest: never reload from map here (selectChat handles it). Reloading on
    // currentChatId change wiped messages when the first guest chat was created.

    if (user && currentChatId && isTempChat(currentChatId)) {
      setMessages(tempMessagesMap.current[currentChatId] || []);
      return () => unsubscribe();
    }

    if (usesFirestoreChat(user, currentChatId, isGuest)) {
      const messagesRef = collection(db, "users", user.uid, "conversations", currentChatId, "messages");
      const q = query(messagesRef, orderBy("createdAt", "asc"));
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (suppressFirestoreSyncRef.current) return;
          const msgs = snapshot.docs.map(messageFromFirestoreDoc);
          setMessages((prev) => mergeSnapshotMessages(prev, msgs));
          const lastUser = [...msgs].reverse().find((m) => isUserSender(m.sender));
          if (lastUser) {
            lastUserSendRef.current = {
              text: lastUser.text || '',
              attachments: lastUser.attachments || [],
              messageId: lastUser.id,
            };
          }
        },
        (err) => {
          console.error("messages snapshot error:", err);
          showToast('Could not load messages. Try refreshing.', 'error');
        }
      );
    }

    return () => unsubscribe();
  }, [currentChatId, user, isGuest]);

  // Persist guest / temp messages into map when they change
  useEffect(() => {
    if (isGuest && currentChatId) {
      const mapped = guestMessagesMap.current[currentChatId];
      if (messages.length === 0 && mapped?.length > 0) return;
      guestMessagesMap.current[currentChatId] = [...messages];
    }
    if (user && currentChatId && isTempChat(currentChatId)) {
      const mapped = tempMessagesMap.current[currentChatId];
      if (messages.length === 0 && mapped?.length > 0) return;
      tempMessagesMap.current[currentChatId] = [...messages];
    }
  }, [isGuest, user, currentChatId, messages]);

  const createNewChat = () => {
    if (isGuest && currentChatId) {
      guestMessagesMap.current[currentChatId] = [...messages];
    }
    messageAnimBaselineRef.current = { chatId: null, count: Number.MAX_SAFE_INTEGER };
    isAwaitingEditedSendRef.current = false;
    setCurrentChatId(null);
    setMessages([]);
    setStreamingReply(null);
    setActiveModel('gpt-4o');
    setSidebarOpen(isMobile ? false : true);
  };

  const startTemporaryChat = () => {
    if (tempChatId) {
      selectChat(tempChatId);
    } else {
      const id = 'temp_' + Date.now();
      setTempChatId(id);
      chatModelMap.current[id] = 'gpt-4o';
      setCurrentChatId(id);
      setMessages([]);
      setStreamingReply(null);
      setActiveModel('gpt-4o');
      setSidebarOpen(isMobile ? false : true);
    }
  };

  const changeModel = (model) => {
    setActiveModel(model);
    if (currentChatId) {
      if (usesFirestoreChat(user, currentChatId, isGuest)) {
        updateDoc(doc(db, "users", user.uid, "conversations", currentChatId), { model }).catch(() => {});
      } else {
        chatModelMap.current[currentChatId] = model;
      }
    }
  };

  const performDeleteChat = async (chatId) => {
    setDeletingChatId(chatId);
    const wasCurrent = currentChatId === chatId;
    const removedChat =
      user && isSafeConversationId(chatId) ? chats.find((c) => c.id === chatId) : null;
    try {
      if (isGuest) {
        delete guestMessagesMap.current[chatId];
        delete chatModelMap.current[chatId];
        setGuestChats((prev) => prev.filter((c) => c.id !== chatId));
      } else if (user && isSafeConversationId(chatId)) {
        setChats((prev) => prev.filter((c) => c.id !== chatId));
      }
      if (wasCurrent) {
        setCurrentChatId(null);
        setMessages([]);
        setStreamingReply(null);
      }
      if (user && isSafeConversationId(chatId)) {
        await deleteFirestoreChat(user.uid, chatId);
      }
    } catch (err) {
      console.error(err);
      if (removedChat) {
        setChats((prev) =>
          prev.some((c) => c.id === chatId) ? prev : [...prev, removedChat]
        );
      }
      showToast('Failed to delete chat.', 'error');
      throw err;
    } finally {
      setDeletingChatId(null);
    }
  };

  const performDeleteAllChats = async () => {
    setIsBulkDeleting(true);
    const chatsBeforeDelete =
      user ? chats.filter((c) => isSafeConversationId(c.id) && !isTempChat(c.id)) : [];
    try {
      if (isGuest) {
        for (const id of guestChats.map((c) => c.id)) {
          delete guestMessagesMap.current[id];
          delete chatModelMap.current[id];
        }
        setGuestChats([]);
      } else if (user) {
        const toDelete = chatsBeforeDelete;
        const deleteIds = new Set(toDelete.map((c) => c.id));
        setChats((prev) => prev.filter((c) => !deleteIds.has(c.id)));
        await runWithConcurrency(toDelete, DELETE_CHATS_CONCURRENCY, (chat) =>
          deleteFirestoreChat(user.uid, chat.id)
        );
      }
      if (tempChatId) {
        delete tempMessagesMap.current[tempChatId];
        delete chatModelMap.current[tempChatId];
        setTempChatId(null);
      }
      setCurrentChatId(null);
      setMessages([]);
      setStreamingReply(null);
    } catch (err) {
      console.error(err);
      if (chatsBeforeDelete.length) setChats(chatsBeforeDelete);
      showToast('Failed to delete all chats.', 'error');
      throw err;
    } finally {
      setDeletingChatId(null);
      setIsBulkDeleting(false);
    }
  };

  const handleDeleteAllChats = () => {
    const count = isGuest
      ? guestChats.length
      : chats.filter(c => isSafeConversationId(c.id) && !isTempChat(c.id)).length;
    if (count === 0) return;
    setConfirmModal({
      open: true,
      title: 'Delete all chats',
      message: `Delete all ${count} chat${count === 1 ? '' : 's'}? This cannot be undone.`,
      confirmLabel: 'Delete all',
      loadingLabel: 'Deleting chats…',
      danger: true,
      loading: false,
      onConfirm: performDeleteAllChats
    });
  };

  const handleConfirmModalAction = async () => {
    if (confirmModal.loading) return;
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      await confirmModal.onConfirm?.();
      setConfirmModal(prev => ({ ...prev, open: false, loading: false }));
    } catch {
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteChat = (e, chatId) => {
    e.stopPropagation();
    if (isTempChat(chatId)) {
      delete tempMessagesMap.current[chatId];
      delete chatModelMap.current[chatId];
      if (tempChatId === chatId) setTempChatId(null);
      if (currentChatId === chatId) {
        setCurrentChatId(null);
        setMessages([]);
        setStreamingReply(null);
      }
      return;
    }
    setConfirmModal({
      open: true,
      title: 'Delete chat',
      message: 'Delete this chat? This cannot be undone.',
      confirmLabel: 'Delete',
      loadingLabel: 'Deleting chat…',
      danger: true,
      loading: false,
      onConfirm: () => performDeleteChat(chatId)
    });
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (isSignUp && password !== confirmPassword) {
      setAuthError("Passwords do not match");
      return;
    }
    try {
      if (isSignUp) {
        if (!firstName) { setAuthError("First Name is required"); return; }
        const res = await createUserWithEmailAndPassword(auth, email, password);
        const displayName = `${firstName} ${lastName}`.trim();
        await updateProfile(res.user, { displayName });
        const accountNumber = await getNextAccountNumber(db);
        await setDoc(doc(db, "users", res.user.uid), {
          accountNumber,
          displayName,
          email,
          createdAt: new Date()
        });
        await sendOberoiVerificationEmail(res.user);
        setResendVerifyCooldown(60);
        setUserProfile({ accountNumber, displayName, email });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      setEmail(''); setPassword(''); setConfirmPassword(''); setFirstName(''); setLastName('');
    } catch (err) {
      const code = err.code || "";
      if (isSignUp) {
        if (code === "auth/email-already-in-use") {
          setAuthError("This email is already in use. Use a different email or login instead.");
        } else if (code === "auth/weak-password") {
          setAuthError("Password is too weak. Use at least 6 characters.");
        } else {
          setAuthError(err.message.replace('Firebase:', '').trim());
        }
      } else if (code === "auth/user-not-found") {
        setAuthError("No account found with this email. Please create an account first.");
      } else if (
        code === "auth/wrong-password" ||
        code === "auth/invalid-credential" ||
        code === "auth/invalid-login-credentials"
      ) {
        setAuthError("Incorrect email or password. Try again or use 'Forgot password?' to reset it.");
      } else if (code === "auth/too-many-requests") {
        setAuthError("Too many attempts. Please wait a moment and try again.");
      } else if (code === "auth/user-disabled") {
        setAuthError("This account has been disabled.");
      } else {
        setAuthError(err.message.replace('Firebase:', '').trim());
      }
    }
  };

  const enterGuestSession = async () => {
    setAuthError('');
    setShowAuthModal(false);
    setShowWelcome(false);

    try {
      if (auth.currentUser) await signOut(auth);
    } catch (_) {
      /* ignore */
    }

    enableLocalGuestSession();
    setIsGuest(true);
    createNewChat();
  };

  const handleGuestMode = () => enterGuestSession();

  const performDeleteAccount = async () => {
    try {
      const currentUser = auth.currentUser;
      await deleteUser(currentUser);
      showToast('Account deleted.', 'success');
    } catch (error) {
      console.error(error);
      showToast('Error deleting account. You may need to re-login first.', 'error');
    }
  };

  const handleDeleteAccount = () => {
    setConfirmModal({
      open: true,
      title: 'Delete account',
      message: 'Are you sure? This will delete your account and all chats permanently.',
      confirmLabel: 'Delete account',
      danger: true,
      onConfirm: performDeleteAccount
    });
  };

  const handleClearMemory = () => {
    if (!user) return;
    setConfirmModal({
      open: true,
      title: "Clear memory",
      message: "This removes everything Oberoi remembers about you across all chats. Your chat history will not be deleted.",
      confirmLabel: "Clear memory",
      loadingLabel: "Clearing memory…",
      danger: true,
      loading: false,
      onConfirm: async () => {
        const previous = userMemory;
        setUserMemory("");
        try {
          const memoryRef = doc(db, "users", user.uid, "memory", MEMORY_DOC_ID);
          await deleteDoc(memoryRef);
          await updateDoc(doc(db, "users", user.uid), { aiMemory: deleteField() });
        } catch (e) {
          setUserMemory(previous);
          console.error("clear memory failed", e);
          showToast('Could not clear memory. Please try again.', 'error');
          throw e;
        }
      }
    });
  };

  const updateMemoryFromExchange = async (uid, userMsg, assistantReply, currentMemory) => {
    if (!userMsg?.trim() || !assistantReply?.trim()) return;
    try {
      const res = await apiFetch(`${API_BASE}/api/openai`, {
        method: "POST",
        body: JSON.stringify({
          model: "gpt-4o",
          messages: [
            {
              role: "system",
              content: `You maintain a short bullet list of important facts about the user that should persist across future chats.
Only keep durable personal facts (health, allergies, name preferences, location, ongoing projects, family, work role, etc.).
Do NOT store greetings, small talk, one-off questions, or temporary moods.
Max 12 bullets. One fact per line starting with "- ".

Current memory:
${(currentMemory || "").trim() || "(none)"}

If the exchange adds no new durable facts, return the current memory unchanged.
Reply with ONLY the updated bullet list. Reply with exactly NONE only if there should be no memory at all.`
            },
            {
              role: "user",
              content: `User: ${userMsg.slice(0, 2000)}\nAssistant: ${assistantReply.slice(0, 2000)}`
            }
          ],
          stream: false
        })
      });
      const data = await res.json();
      const raw = data?.choices?.[0]?.message?.content;
      if (typeof raw !== "string") return;
      const updated = raw.trim();
      const memoryRef = doc(db, "users", uid, "memory", MEMORY_DOC_ID);
      if (/^none$/i.test(updated)) {
        if ((currentMemory || "").trim()) {
          await deleteDoc(memoryRef);
          setUserMemory("");
        }
        return;
      }
      if (!updated) return;
      await setDoc(memoryRef, { content: updated, updatedAt: serverTimestamp() }, { merge: true });
      setUserMemory(updated);
    } catch (e) {
      console.warn("updateMemoryFromExchange failed", e);
    }
  };

  const performLogout = async () => {
    setIsGuest(false);
    disableLocalGuestSession();
    if (auth.currentUser) {
      await signOut(auth);
    }
    setUserProfile(null);
    setUserMemory("");
    setGuestChats([]);
    guestMessagesMap.current = {};
    chatModelMap.current = {};
    setMessages([]);
    setCurrentChatId(null);
    setStreamingReply(null);
    setShowWelcome(true);
  };

  const handleLogoutClick = () => {
    const guestSession = isGuest;
    setConfirmModal({
      open: true,
      title: guestSession ? 'Exit guest mode?' : 'Log out?',
      message: guestSession
        ? 'You will leave guest mode and return to the welcome screen. Chats from this session are not saved to your account.'
        : 'You will be signed out of your Oberoi account on this device.',
      confirmLabel: guestSession ? 'Exit guest' : 'Log out',
      loadingLabel: 'Signing out…',
      danger: true,
      loading: false,
      onConfirm: performLogout,
    });
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordError("");
    const emailVal = forgotPasswordEmail.trim();
    if (!emailVal) {
      setForgotPasswordError("Enter your email.");
      return;
    }
    try {
      await sendOberoiPasswordResetEmail(emailVal);
      setForgotPasswordSent(true);
    } catch (err) {
      setForgotPasswordError(err.message?.replace("Firebase:", "").trim() || "Failed to send reset email.");
    }
  };

  const handleResendVerifyEmail = async () => {
    if (!user || resendVerifyCooldown > 0) return;
    try {
      await sendOberoiVerificationEmail(user);
      setResendVerifySent(true);
      setResendVerifyCooldown(60);
      setAuthError("");
    } catch (err) {
      setAuthError(err.message?.replace("Firebase:", "").trim() || "Failed to resend.");
    }
  };

  const handleUseDifferentEmail = async () => {
    setAuthError("");
    setResendVerifySent(false);
    setVerifyCheckMessage("");
    setResendVerifyCooldown(0);
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setIsSignUp(false);
    try {
      await signOut(auth);
    } catch (err) {
      showToast("Could not sign out. Try again.", "error");
    }
  };

  const handleGuestFromVerify = async () => {
    setResendVerifySent(false);
    setVerifyCheckMessage("");
    try {
      await signOut(auth);
    } catch (err) {
      console.warn('Sign out before guest failed', err);
    }
    await enterGuestSession();
  };

  const handleCheckVerified = () => {
    if (!user) return;
    setAuthError("");
    setVerifyCheckMessage("checking");
    user.reload().then(() => {
      const updated = auth.currentUser;
      setTimeout(() => {
        if (updated?.emailVerified) {
          setVerifyCheckMessage(null);
          setUser(updated);
          ensureUserProfileAndLoadChats(updated.uid, updated);
        } else {
          setVerifyCheckMessage("not_yet");
        }
      }, 2000);
    }).catch(() => {
      setVerifyCheckMessage("not_yet");
    });
  };

  useEffect(() => {
    if (resendVerifyCooldown <= 0) return;
    const t = setInterval(() => setResendVerifyCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [resendVerifyCooldown]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError("");
    if (changePasswordNew.length < 6) {
      setChangePasswordError("New password must be at least 6 characters.");
      return;
    }
    if (changePasswordNew !== changePasswordConfirm) {
      setChangePasswordError("New passwords do not match.");
      return;
    }
    const currentUser = auth.currentUser;
    if (!currentUser?.email) {
      setChangePasswordError("You must be signed in to change password.");
      return;
    }
    setChangePasswordLoading(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, changePasswordCurrent);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, changePasswordNew);
      setShowChangePasswordModal(false);
      setChangePasswordCurrent("");
      setChangePasswordNew("");
      setChangePasswordConfirm("");
      showToast('Password updated successfully.', 'success');
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("wrong-password") || msg.includes("invalid-credential")) {
        setChangePasswordError("Current password is incorrect.");
      } else {
        setChangePasswordError(msg.replace("Firebase:", "").trim());
      }
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // --- Chat & UI Logic ---

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth > 768) setSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollMessagesToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, []);

  // Snap to latest messages instantly (no smooth scroll on chat open / history load)
  useLayoutEffect(() => {
    scrollMessagesToBottom();
    requestAnimationFrame(scrollMessagesToBottom);
    messageAnimBaselineRef.current = {
      chatId: currentChatId,
      count: messages.length + (streamingReply ? 1 : 0),
    };
  }, [messages, streamingReply, currentChatId, scrollMessagesToBottom]);

  useEffect(() => {
    setOpenWebSourcesKey(null);
    setWebSourcesAboveKey(null);
  }, [messages, streamingReply, isLoading, isRegenerating]);

  useEffect(() => {
    if (!openWebSourcesKey) return undefined;

    const closeOnOutsideInteraction = (event) => {
      if (!webSourcesContainerRef.current?.contains(event.target)) {
        setOpenWebSourcesKey(null);
        setWebSourcesAboveKey(null);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenWebSourcesKey(null);
        setWebSourcesAboveKey(null);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideInteraction);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideInteraction);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [openWebSourcesKey]);

  useEffect(() => {
    const el = inputTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);


  // Voice Logic (condensed)
  const loadVoices = () => {
    let voices = window.speechSynthesis.getVoices().filter(v => v.lang.includes('en'));
    const fixedNames = ['Mark', 'Emma', 'David', 'Zira', 'Samantha'];
    const patterns = [['Google US English', 'Mark', 'Male'], ['Google UK English Female', 'Emma', 'Female UK'], ['David', 'Microsoft David'], ['Zira', 'Microsoft Zira'], ['Samantha']];
    const selectedVoices = [];
    patterns.forEach((patternList) => {
      const match = voices.find(v => patternList.some(p => v.name.includes(p)));
      if (match && !selectedVoices.includes(match)) selectedVoices.push(match);
    });
    if (selectedVoices.length < 5) {
      const extra = voices.filter(v => !selectedVoices.includes(v)).slice(0, 5 - selectedVoices.length);
      selectedVoices.push(...extra);
    }
    setAvailableVoices(selectedVoices.slice(0, 5));
    setSelectedVoiceIndex(0);
  };

  useEffect(() => {
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    setTimeout(loadVoices, 1000);
  }, []);

  const displayVoiceNames = ['Mark', 'Emma', 'David', 'Zira', 'Samantha'];

  useEffect(() => {
    if (!voiceMode) {
      window.speechSynthesis.cancel();
      speechBuffer.current = '';
      isSpeaking.current = false;
    }
  }, [voiceMode]);

  const filterCodeForSpeech = (text) => text.replace(/```[\s\S]*?```/g, '').replace(/`.*?`/g, '').trim();

  const processSpeechBuffer = (forceFinal = false) => {
    if (!voiceMode || isSpeaking.current || availableVoices.length === 0) return;
    const match = speechBuffer.current.match(/[.!?\n]/);
    if (match || (forceFinal && speechBuffer.current.trim().length > 0)) {
      let chunk = match ? speechBuffer.current.substring(0, match.index + 1) : speechBuffer.current;
      speechBuffer.current = match ? speechBuffer.current.substring(match.index + 1) : '';
      const toSpeak = filterCodeForSpeech(chunk);
      if (!toSpeak) { processSpeechBuffer(); return; }
      const utterance = new SpeechSynthesisUtterance(toSpeak);
      utterance.voice = availableVoices[selectedVoiceIndex] || availableVoices[0];
      utterance.rate = 0.95;
      utterance.onstart = () => { isSpeaking.current = true; };
      utterance.onend = () => { isSpeaking.current = false; processSpeechBuffer(); };
      utterance.onerror = () => { isSpeaking.current = false; };
      window.speechSynthesis.speak(utterance);
    }
  };

  const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });

  const processOneFile = async (file, isCamera = false) => {
    const name = file.name || '';
    const lower = name.toLowerCase();

    if (file.size > MAX_FILE_BYTES) {
      throw new Error(`"${name}" is too large (max ${MAX_FILE_BYTES / (1024 * 1024)}MB per file).`);
    }

    if (file.type.startsWith('image/')) {
      const { full, preview } = await resizeImage(file);
      const imageId = newImageId();
      storeImage(imageId, { imageData: full, preview });
      return {
        type: 'image',
        kind: 'image',
        imageId,
        preview,
        fileName: name || 'camera-photo.jpg',
      };
    }

    if (file.type === 'application/pdf' || lower.endsWith('.pdf')) {
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      let pdfText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        pdfText += textContent.items.map((item) => item.str).join(' ') + '\n';
      }
      const trimmed = truncateForModel(pdfText.trim(), name);
      if (!trimmed) throw new Error('No text found in PDF.');
      return { type: 'pdf', kind: 'pdf', extractedText: trimmed, fileName: name };
    }

    if (
      file.type === 'text/plain' ||
      file.type === 'text/csv' ||
      file.type === 'application/json' ||
      lower.endsWith('.txt') ||
      lower.endsWith('.csv') ||
      lower.endsWith('.json')
    ) {
      const docText = truncateForModel(await readFileAsText(file), name);
      return formatDocAttachment(name, docText, kindFromFileName(name));
    }

    const isDocx =
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      lower.endsWith('.docx');
    const isDocLegacy =
      (file.type === 'application/msword' || lower.endsWith('.doc')) && !lower.endsWith('.docx');
    const isExcel =
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      /\.(xls|xlsx)$/i.test(name);
    const isPptx =
      file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      lower.endsWith('.pptx');
    const isPptLegacy =
      (file.type === 'application/vnd.ms-powerpoint' || lower.endsWith('.ppt')) && !lower.endsWith('.pptx');

    if (file.type.startsWith('video/') || /\.(mp4|webm|mov|m4v|mkv|avi)$/i.test(name)) {
      throw new Error('Videos are not supported.');
    }
    if (isDocLegacy) throw new Error('Older .doc files are not supported. Save as .docx and try again.');
    if (isPptLegacy) throw new Error('Older .ppt files are not supported. Save as .pptx and try again.');
    if (isDocx) {
      const docText = await extractDocxText(file);
      return formatDocAttachment(name, docText, 'word');
    }
    if (isExcel) {
      const docText = await extractExcelText(file);
      return formatDocAttachment(name, docText, 'excel');
    }
    if (isPptx) {
      const docText = await extractPptxText(file);
      return formatDocAttachment(name, docText, 'ppt');
    }
    if (file.type.startsWith('text/') || file.size < 500 * 1024) {
      try {
        const docText = truncateForModel(await readFileAsText(file), name);
        return formatDocAttachment(name, docText, kindFromFileName(name));
      } catch (_) {
        throw new Error('Could not read this file as text.');
      }
    }
    throw new Error('Unsupported file type.');
  };

  const handleFileSelect = async (e, isCamera = false) => {
    const files = isCamera
      ? (e.target.files?.[0] ? [e.target.files[0]] : [])
      : Array.from(e.target.files || []);
    if (!files.length || isProcessingFile) return;
    e.target.value = '';

    const slots = MAX_ATTACHMENTS - attachedFiles.length;
    if (slots <= 0) {
      showToast(`Maximum ${MAX_ATTACHMENTS} files per message.`, 'error');
      return;
    }
    const toProcess = files.slice(0, slots);
    if (files.length > slots) {
      showToast(`Only ${slots} more file(s) added (max ${MAX_ATTACHMENTS} per message).`, 'info');
    }

    setIsProcessingFile(true);
    const added = [];
    try {
      for (const file of toProcess) {
        setProcessingFileName(file.name || 'file');
        const attach = await processOneFile(file, isCamera);
        added.push(attach);
      }
      if (added.length) setAttachedFiles((prev) => [...prev, ...added]);
    } catch (err) {
      showToast(err?.message || 'Failed to process file.', 'error');
    } finally {
      setIsProcessingFile(false);
      setProcessingFileName('');
    }
  };

  const resizeImage = (file) => {
    const render = (src, maxWidth, quality) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = src;
      });

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const src = event.target.result;
        const full = await render(src, 512, 0.72);
        const preview = await render(src, 72, 0.62);
        resolve({ full, preview });
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachedFile = (index) => {
    setAttachedFiles((prev) => {
      const target = prev[index];
      if (target?.imageId) removeImage(target.imageId);
      return prev.filter((_, i) => i !== index);
    });
  };

  const openAttachmentPreview = (attach) => {
    if (getAttachmentKind(attach) !== 'image') return;
    const resolved = resolveAttachmentsForDisplay([attach])[0];
    const src = resolved?.imageData || resolved?.preview || attach.content;
    if (src) setExpandedImage(src);
  };

  const stopStreaming = () => {
    if (abortController.current) abortController.current.abort();
    setIsLoading(false);

    const streamChatId = activeStreamChatIdRef.current || currentChatIdRef.current;
    activeStreamChatIdRef.current = null;

    // Save partial reply if exists
    if (streamingReply && streamingReply.text && streamChatId) {
      const finalMsg = {
        text: streamingReply.text,
        sender: 'Oberoi',
      };
      const partial = {
        id: 'latest_ai_reply_' + Date.now(),
        ...finalMsg,
        createdAt: new Date(),
      };
      patchMessagesForChat(streamChatId, (prev) => [...prev, partial]);
      saveMessageToDB(finalMsg, streamChatId);
    }
    setStreamingReply(null);
  };

  // Save message: Firestore for saved account chats only (never guest / temp)
  const saveMessageToDB = async (msgData, chatId = currentChatIdRef.current) => {
    if (isGuest || (chatId && isTempChat(chatId))) return;
    if (!usesFirestoreChat(user, chatId, isGuest)) return;

    const msgWithTimestamp = prepareMessageForFirestore({ ...msgData, createdAt: new Date() });
    const messagesRef = collection(db, "users", user.uid, "conversations", chatId, "messages");
    await addDoc(messagesRef, msgWithTimestamp);
  };

  const copyMessageText = (text) => {
    const t = (text || '').trim();
    if (!t) return;
    navigator.clipboard.writeText(t).then(
      () => showToast('Copied to clipboard.', 'success'),
      () => showToast('Could not copy.', 'error')
    );
  };

  const toggleWebSources = (event, messageKey, sourceCount) => {
    if (openWebSourcesKey === messageKey) {
      setOpenWebSourcesKey(null);
      setWebSourcesAboveKey(null);
      return;
    }

    const buttonRect = event.currentTarget.getBoundingClientRect();
    const dropdownHeight = Math.min(220, sourceCount * 58 + 10);
    const opensAbove = window.innerHeight - buttonRect.bottom < dropdownHeight + 12;
    setWebSourcesAboveKey(opensAbove ? messageKey : null);
    setOpenWebSourcesKey(messageKey);
  };

  const handleRegenerate = async () => {
    if (isLoading || isProcessingFile || isRegenerating) return;
    const ctx = buildRegenerateContext(messages, lastUserSendRef.current);
    if (!ctx) {
      showToast('No reply to regenerate yet.', 'info');
      return;
    }

    const { text, attachments, lastAi, removedReply, history: trimmed } = ctx;

    suppressFirestoreSyncRef.current = true;
    setIsRegenerating(true);
    setStreamingReply(null);
    setMessages(trimmed);

    if (isGuest && currentChatId) {
      guestMessagesMap.current[currentChatId] = trimmed;
    }
    if (user && currentChatId && isTempChat(currentChatId)) {
      tempMessagesMap.current[currentChatId] = trimmed;
    }

    try {
      if (usesFirestoreChat(user, currentChatId, isGuest) && isFirestoreMessageId(removedReply?.id)) {
        await deleteDoc(
          doc(db, 'users', user.uid, 'conversations', currentChatId, 'messages', removedReply.id)
        );
        await updateDoc(doc(db, 'users', user.uid, 'conversations', currentChatId), {
          updatedAt: new Date(),
        });
      }

      regenerateSnapshotRef.current = {
        text,
        attachments,
        history: trimmed,
      };
      await sendMessage({ regenerate: true });
    } catch (err) {
      console.error('Regenerate failed', err);
      showToast('Could not regenerate reply.', 'error');
    } finally {
      suppressFirestoreSyncRef.current = false;
      setIsRegenerating(false);
    }
  };

  const handleEditLastPrompt = async () => {
    if (isLoading || isProcessingFile || isRegenerating || streamingReply) return;

    const lastUserIdx = findLastUserMessageIndex(messages);
    if (lastUserIdx < 0) {
      showToast('No message to edit.', 'info');
      return;
    }

    const userMsg = messages[lastUserIdx];
    const kept = messages.slice(0, lastUserIdx);
    const promptText = (userMsg.text || '').trim();

    const ref = lastUserSendRef.current;
    let attachmentsForEdit = [];
    if (ref && (ref.text || '').trim() === promptText) {
      attachmentsForEdit = ref.attachments || [];
    } else {
      attachmentsForEdit = resolveAttachmentsForApi(userMsg.attachments || []);
    }

    suppressFirestoreSyncRef.current = true;
    isAwaitingEditedSendRef.current = true;
    setStreamingReply(null);

    try {
      setMessages(kept);
      if (isGuest && currentChatId) {
        guestMessagesMap.current[currentChatId] = kept;
      }
      if (user && currentChatId && isTempChat(currentChatId)) {
        tempMessagesMap.current[currentChatId] = kept;
      }
      if (usesFirestoreChat(user, currentChatId, isGuest)) {
        await applyFirestoreEditTrim(user, currentChatId, messages, lastUserIdx);
      }

      lastUserSendRef.current = null;
      setInput(userMsg.text || '');
      setAttachedFiles(attachmentsForEdit);
      requestAnimationFrame(() => inputTextareaRef.current?.focus());
      showToast('Edit your message below, then send.', 'info');
    } catch (err) {
      console.error('Edit failed', err);
      isAwaitingEditedSendRef.current = false;
      showToast('Could not restore message for editing.', 'error');
    } finally {
      suppressFirestoreSyncRef.current = false;
    }
  };

  const sendMessage = async (options = {}) => {
    const isRegenerate = options?.regenerate === true;
    let userMsg;
    let attachmentsForApi;
    let historyForRequest = messages;
    let isEditedResend = false;

    if (isRegenerate) {
      const snap = regenerateSnapshotRef.current;
      if (!snap) return;
      regenerateSnapshotRef.current = null;
      userMsg = (snap.text || '').trim();
      attachmentsForApi = snap.attachments || [];
      historyForRequest = snap.history || [];
      if (!userMsg && attachmentsForApi.length === 0) return;
    } else {
      if ((!input.trim() && attachedFiles.length === 0) || isLoading || isProcessingFile) return;
      userMsg = input.trim();
      attachmentsForApi = attachedFiles;
      setInput('');
      setAttachedFiles([]);

      if (isAwaitingEditedSendRef.current) {
        isAwaitingEditedSendRef.current = false;
        isEditedResend = true;
        historyForRequest = messages;
      }
    }

    const holdFirestoreSync =
      isEditedResend && usesFirestoreChat(user, currentChatId, isGuest);
    if (holdFirestoreSync) suppressFirestoreSyncRef.current = true;

    const attachmentsStored = attachmentsForApi.map(sanitizeAttachmentForStorage);
    if (!isRegenerate) {
      lastUserSendRef.current = {
        text: userMsg,
        attachments: attachmentsForApi,
        messageId: null,
      };
    }

    setIsLoading(true);

    let targetChatId = currentChatId;
    if (!targetChatId) {
      if (isGuest) {
        const newId = 'guest_chat_' + Date.now();
        const title = getConversationTitle(userMsg);
        chatModelMap.current[newId] = activeModel;
        guestMessagesMap.current[newId] = [];
        setGuestChats(prev => [...prev, { id: newId, title: title || 'New Chat', updatedAt: Date.now() }]);
        setCurrentChatId(newId);
        currentChatIdRef.current = newId;
        targetChatId = newId;
      } else if (user) {
        const conversationsRef = collection(db, "users", user.uid, "conversations");
        const title = getConversationTitle(userMsg);
        const docRef = await addDoc(conversationsRef, {
          title,
          model: activeModel,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setCurrentChatId(docRef.id);
        currentChatIdRef.current = docRef.id;
        targetChatId = docRef.id;
      }
    }
    activeSendChatIdRef.current = targetChatId;

    // 1. OPTIMISTIC USER UPDATE (skip on regenerate)
    let optimisticUserMsg = null;
    if (!isRegenerate) {
      optimisticUserMsg = {
        id: 'opt_' + Date.now(),
        text: userMsg,
        sender: 'You',
        userId: user?.uid || 'guest',
        attachments: attachmentsStored,
        createdAt: new Date()
      };
      patchMessagesForChat(targetChatId, (prev) => [...prev, optimisticUserMsg]);
    }

    // 2. SAVE TO DB — saved account chats only (not guest / temp)
    if (!isRegenerate && optimisticUserMsg && usesFirestoreChat(user, targetChatId, isGuest)) {
      const messagesRef = collection(db, "users", user.uid, "conversations", targetChatId, "messages");
      try {
        suppressFirestoreSyncRef.current = true;
        const savedRef = await addDoc(messagesRef, prepareMessageForFirestore(optimisticUserMsg));
        patchMessagesForChat(targetChatId, (prev) =>
          prev.map((m) => (m.id === optimisticUserMsg.id ? { ...m, id: savedRef.id } : m))
        );
        lastUserSendRef.current = {
          text: userMsg,
          attachments: attachmentsForApi,
          messageId: savedRef.id,
        };
      } catch (e) {
        console.error("Save failed", e);
        showToast("Message saved locally but cloud sync may have failed.", "error");
      } finally {
        if (!holdFirestoreSync) suppressFirestoreSyncRef.current = false;
      }

      if (historyForRequest.length === 0) {
        const initialTitle = getConversationTitle(userMsg);
        updateDoc(doc(db, "users", user.uid, "conversations", targetChatId), {
          title: initialTitle,
          updatedAt: new Date()
        });
        generateSpecificTitle(userMsg).then((specific) => {
          if (specific && user && targetChatId && isSafeConversationId(targetChatId)) {
            updateDoc(doc(db, "users", user.uid, "conversations", targetChatId), {
              title: specific,
              updatedAt: new Date()
            });
          }
        });
      }
    } else if (!isRegenerate && isGuest && historyForRequest.length === 0) {
      const initialTitle = getConversationTitle(userMsg);
      setGuestChats(prev => prev.map(c => c.id === targetChatId ? { ...c, title: initialTitle || "New Chat", updatedAt: Date.now() } : c));
      generateSpecificTitle(userMsg).then((specific) => {
        if (specific) {
          setGuestChats(prev => prev.map(c => c.id === targetChatId ? { ...c, title: specific, updatedAt: Date.now() } : c));
        }
      });
    }
    
    // 3. START STREAM
    activeStreamChatIdRef.current = targetChatId;
    setStreamingReply({ text: '', isStreaming: true });
    abortController.current = new AbortController();

    try {
      let searchContext = '';
      let webSources = [];
      if (activeModel === 'gpt-5.2') {
        try {
          devLog('Running Tavily search...');
          const res = await apiFetch(`${TAVILY_PROXY}/api/search`, {
            method: 'POST',
            body: JSON.stringify({ query: userMsg })
          });
          const data = await res.json();
          devLog('Tavily results:', data);
          if (data.results && data.results.length > 0) {
            webSources = normalizeWebSources(data.results);
            searchContext = '\n\n=== LATEST WEB SEARCH RESULTS ===\n';
            if (typeof data.answer === 'string' && data.answer.trim()) {
              searchContext += `Search answer: ${data.answer.trim()}\n\n`;
            }
            data.results.forEach((r, i) => {
              searchContext += `${i+1}. Title: ${r.title}\nContent: ${r.content}\nSource: ${r.url}\n\n`;
            });
            searchContext += `Use these results as supporting context. Answer the user's question directly, without narrating the search. If the results do not contain enough detail about a specific page or document, ask the user to share its link.`;
          }
        } catch (err) {
          console.error('Tavily failed:', err);
        }
      }

      const userContent = buildUserApiContent(userMsg, attachmentsForApi, searchContext);
      const hasFiles = attachmentsForApi.length > 0;
      const apiModel = needsVisionModel(attachmentsForApi) ? 'gpt-4o' : activeModel;
      const systemPrompt = buildSystemPrompt(
        usesFirestoreChat(user, targetChatId, isGuest) ? userMemory : '',
        hasFiles,
        activeModel === 'gpt-5.2'
      );
      const runtimeInstructions = activeModel === 'gpt-5.2'
        ? buildV2RuntimeInstructions()
        : null;
      const response = await apiFetch(`${API_BASE}/api/openai`, {
        method: 'POST',
        body: JSON.stringify({
          model: apiModel,
          messages: [
            { role: 'system', content: systemPrompt },
            ...(runtimeInstructions ? [{ role: 'system', content: runtimeInstructions }] : []),
            ...historyForRequest.map(m => ({
              role: isUserSender(m.sender) ? 'user' : 'assistant',
              content: messageToApiContent(m),
            })),
            { role: 'user', content: userContent || userMsg }
          ],
          stream: true
        }),
        signal: abortController.current.signal
      });

      if (!response.ok) {
        throw new Error(await readApiErrorMessage(response));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullReply = '';
      let buffer = '';

      const processLine = (line) => {
        const trimmed = line.trim();
        if (!trimmed || trimmed === 'data: [DONE]') return;
        if (trimmed.startsWith('data: ')) {
          try {
            const json = JSON.parse(trimmed.slice(6));
            if (json.error) {
              const errDetail =
                json.error?.message ||
                (typeof json.error === 'string' ? json.error : null) ||
                json.message;
              throw new Error(errDetail || 'The AI service returned an error. Please try again.');
            }
            let delta = json.choices?.[0]?.delta?.content ?? json.choices?.[0]?.message?.content ?? '';
            if (typeof delta !== 'string') delta = '';
            if (delta) {
              fullReply += delta;
              speechBuffer.current += delta;
              processSpeechBuffer();
              setStreamingReply({ text: fullReply, isStreaming: true });
            }
          } catch (e) {
            if (e instanceof SyntaxError) { /* skip bad JSON */ }
            else throw e;
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        const chunk = value ? decoder.decode(value, { stream: !done }) : '';
        if (chunk) buffer += chunk;
        let lineEnd;
        while ((lineEnd = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, lineEnd + 1);
          buffer = buffer.slice(lineEnd + 1);
          processLine(line);
        }
        if (done) {
          if (buffer.trim()) processLine(buffer);
          break;
        }
      }

      processSpeechBuffer(true);

      // 4. STREAM COMPLETE -> OPTIMISTIC UPDATE + SAVE
      setStreamingReply(null);

      const authoritativeTimeReply =
        activeModel === 'gpt-5.2' ? getAuthoritativeIndiaTimeReply(userMsg) : null;
      const replyText = authoritativeTimeReply ||
        ((fullReply && fullReply.trim()) ? fullReply.trim() : "Oberoi didn't return a reply. Please try again.");
      const finalAiMsg = {
        id: 'ai_' + Date.now(),
        text: replyText,
        sender: 'Oberoi',
        userId: user?.uid || 'guest',
        webSources,
        createdAt: new Date()
      };

      patchMessagesForChat(targetChatId, (prev) => [...prev, finalAiMsg]);

      // Usage: local counter for guest/temp; Firestore only for saved account chats
      const systemContent = buildSystemPrompt(
        usesFirestoreChat(user, targetChatId, isGuest) ? userMemory : '',
        hasFiles,
        activeModel === 'gpt-5.2'
      ) + (runtimeInstructions ? `\n${runtimeInstructions}` : '');
      const convChars = historyForRequest.map(m => JSON.stringify(messageToApiContent(m)).length).reduce((a, b) => a + b, 0);
      const userContentLen = JSON.stringify(userContent || userMsg || '').length;
      const inputChars = systemContent.length + convChars + userContentLen;
      const costUsd = getUsageCostUsd(apiModel, inputChars, fullReply.length);
      setTotalUsageUsd(prev => (prev || 0) + costUsd);
      if (usesFirestoreChat(user, targetChatId, isGuest)) {
        try {
          const userRef = doc(db, "users", user.uid);
          await updateDoc(userRef, { totalUsageUsd: increment(costUsd) });
        } catch (e) {
          devWarn("Usage update failed", e);
        }
      }

      if (usesFirestoreChat(user, targetChatId, isGuest)) {
        const messagesRef = collection(db, "users", user.uid, "conversations", targetChatId, "messages");
        suppressFirestoreSyncRef.current = true;
        try {
          const savedRef = await addDoc(messagesRef, prepareMessageForFirestore(finalAiMsg));
          await updateDoc(doc(db, "users", user.uid, "conversations", targetChatId), {
            updatedAt: new Date(),
          });
          patchMessagesForChat(targetChatId, (prev) =>
            dedupeMessages(
              prev.map((m) => (m.id === finalAiMsg.id ? { ...m, id: savedRef.id } : m))
            )
          );
        } finally {
          if (!holdFirestoreSync) suppressFirestoreSyncRef.current = false;
        }
        updateMemoryFromExchange(user.uid, userMsg, replyText, userMemory);
      }

    } catch (err) {
      setStreamingReply(null);
      if (err.name !== 'AbortError') {
        const errMsg = humanizeClientError(err);
        patchMessagesForChat(targetChatId, (prev) => [
          ...prev,
          {
            id: 'err_' + Date.now(),
            text: errMsg,
            sender: 'system',
            isError: true,
            userId: user?.uid || 'guest',
            createdAt: new Date(),
          },
        ]);
        showToast(errMsg, 'error');
      }
    } finally {
      activeStreamChatIdRef.current = null;
      activeSendChatIdRef.current = null;
      if (holdFirestoreSync) suppressFirestoreSyncRef.current = false;
      setIsLoading(false);
      abortController.current = null;
    }
  };

  // Numerator/denominator are "simple" (single number or identifier) → no brackets needed for fraction
  const isSimpleForFraction = (t) => {
    const x = (t || '').trim();
    return x.length > 0 && /^[a-zA-Z0-9.-]+$/.test(x) && !/[\s+*\/]/.test(x);
  };

  // Strip/simplify LaTeX — no raw \frac, \( \), \leq; brackets only when necessary; division / kept
  const stripLatexForDisplay = (str) => {
    let s = str
      .replace(/\\\\/g, '\u200B')
      .replace(/\\\(/g, '')
      .replace(/\\\)/g, '')
      .replace(/\\\[/g, '')
      .replace(/\\\]/g, '')
      .replace(/\\ /g, ' ')
      .replace(/\\begin\{[^}]*\}/g, '')
      .replace(/\\end\{[^}]*\}/g, '')
      .replace(/\\left\s*\(/g, '(')
      .replace(/\\right\s*\)/g, ')')
      .replace(/\\left\s*\[/g, '[')
      .replace(/\\right\s*\]/g, ']')
      .replace(/\\times/g, '×')
      .replace(/\\cdot/g, '·')
      .replace(/\\div/g, '÷')
      .replace(/\\leq/g, '≤')
      .replace(/\\geq/g, '≥')
      .replace(/\\neq/g, '≠')
      .replace(/\\approx/g, '≈')
      .replace(/\\pm/g, '±')
      .replace(/\\mp/g, '∓')
      .replace(/\\infty/g, '∞')
      .replace(/\\implies/g, '⇒')
      .replace(/\\Rightarrow/g, '⇒')
      .replace(/\\rightarrow/g, '→')
      .replace(/\\leftarrow/g, '←')
      .replace(/\\quad/g, ' ')
      .replace(/\\qquad/g, ' ');
    // \frac: use brackets only when numerator or denominator is not simple (division slash always kept)
    for (let i = 0; i < 3; i++) {
      s = s.replace(/\\sqrt\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, (_, inner) => '√(' + inner + ')');
      s = s.replace(/\\frac\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, (_, a, b) => {
        const num = a.trim(), den = b.trim();
        if (isSimpleForFraction(num) && isSimpleForFraction(den)) return num + '/' + den;
        return '(' + num + ')/(' + den + ')';
      });
    }
    s = s.replace(/\\([a-zA-Z*]+)/g, '');
    s = s.replace(/\u200B/g, '\\').replace(/\\/g, '');
    s = s.replace(/\(\s*\)/g, '');
    s = s.replace(/\[\s*\]/g, '');
    return s;
  };

  const parseText = (text) => {
    const noLatex = stripLatexForDisplay(text);
    return noLatex.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>').replace(/__(.*?)__/g, '<u>$1</u>');
  };

  // True if this text has a lot of LaTeX/math logic → show in IDE-style block
  const isHeavyMath = (part) => {
    const backslashCount = (part.match(/\\/g) || []).length;
    return backslashCount > 3 || /\\(frac|sqrt|begin|left|right|\[|\()/.test(part);
  };

  const renderTextContent = (text) => {
    const parts = (text || '').split(/```/);
    return parts.map((part, i) => {
      if (i % 2 === 1) return <CodeBlock key={`code-${i}`} code={part.trim()} />;
      const isHeavy = isHeavyMath(part);
      const displayed = parseText(part);
      if (isHeavy) {
        return (
          <div key={`text-${i}`} className="code-container" style={{ marginTop: 6, marginBottom: 6 }}>
            <div className="code-content" style={{ padding: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: displayed }} />
          </div>
        );
      }
      return <span key={`text-${i}`} dangerouslySetInnerHTML={{ __html: displayed }} />;
    });
  };

  const renderContent = (text, attachments = [], isStreaming = false) => {
    if (isStreaming && !text) {
      return <div className="thinking-indicator">Oberoi is thinking<span className="dots">...</span></div>;
    }
    const hasText = (text || '').trim().length > 0;
    return (
      <div className="message-content-inner">
        {attachments.length > 0 && (
          <div className="message-attachments">
            {resolveAttachmentsForDisplay(attachments).map((attach, index) => (
              <AttachmentChip
                key={`at-${index}`}
                attach={attach}
                onPreviewClick={openAttachmentPreview}
              />
            ))}
          </div>
        )}
        {hasText && <div className="message-text">{renderTextContent(text)}</div>}
      </div>
    );
  };

  // --- RENDER ---
  const allChats = user
    ? (tempChatId ? [{ id: tempChatId, title: 'Temporary chat (not saved)', updatedAt: Date.now() }, ...chats] : chats)
    : [...guestChats].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  const displayChats = chatSearchQuery.trim()
    ? allChats.filter(c => (c.title || '').toLowerCase().includes(chatSearchQuery.trim().toLowerCase()))
    : allChats;
  const savedChatCount = isGuest
    ? guestChats.length
    : chats.filter(c => isSafeConversationId(c.id) && !isTempChat(c.id)).length;
  const currentChatTitle = allChats.find(c => c.id === currentChatId)?.title || (currentChatId && isTempChat(currentChatId) ? 'Temporary chat (not saved)' : 'New Chat');
  // Header: short title only (no "(not saved)" etc. — reads like stray brackets in the corner)
  const headerChatTitle = currentChatId
    ? (isTempChat(currentChatId)
      ? 'Temporary chat'
      : (currentChatTitle || '').replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim() || currentChatTitle)
    : '';

  const displayMessages = dedupeMessages([...messages]);
  if (streamingReply) {
    displayMessages.push({
      sender: 'Oberoi',
      text: streamingReply.text,
      isStreaming: true,
      id: 'streaming_now'
    });
  }

  let lastUserDisplayIdx = -1;
  let lastOberoiDisplayIdx = -1;
  const normalizeSender = (sender) => {
    if (sender === 'You' || sender === 'user') return 'You';
    if (sender === 'Oberoi' || sender === 'assistant') return 'Oberoi';
    return sender;
  };

  const messageAnimBaseline = messageAnimBaselineRef.current;
  const animateMessageFromIndex =
    messageAnimBaseline.chatId === currentChatId ? messageAnimBaseline.count : displayMessages.length;

  displayMessages.forEach((msg, i) => {
    const sender = normalizeSender(msg.sender);
    if (sender === 'You') lastUserDisplayIdx = i;
    if (
      sender === 'Oberoi' &&
      !msg.isStreaming &&
      ((msg.text || '').trim() || (msg.attachments || []).length > 0)
    ) {
      lastOberoiDisplayIdx = i;
    }
  });

  const canEditPrompt =
    !isLoading && !isProcessingFile && !isRegenerating && !streamingReply;
  const canRegenerate =
    canEditPrompt &&
    lastOberoiDisplayIdx >= 0 &&
    lastOberoiDisplayIdx > lastUserDisplayIdx;

  if (authLoading && !isGuest) {
    return (
      <>
        <ToastContainer />
        <div className="landing-container auth-loading-screen">
          <p className="auth-hint">Loading…</p>
        </div>
      </>
    );
  }

  // Email verification required — no app access until verified (guest is separate)
  if (user && !user.isAnonymous && !user.emailVerified) {
    return (
      <>
      <ToastContainer />
      <div className="landing-container">
        <div className="auth-box">
          <h2 className="auth-title">Verify your email</h2>
          <p className="auth-hint verify-email-hint">We sent a link to <strong>{user.email}</strong>. Click the link in that email to verify your account. <span className="verify-check-spam">Check spam</span> if you don&apos;t see it.</p>
          {isCustomAuthEmailEnabled() && (
            <p className="email-noreply-hint">
              Look for an email from <strong>{getAuthEmailFromLabel()}</strong> in your inbox or spam.
            </p>
          )}
          {authError && <div className="auth-error">{authError}</div>}
          {resendVerifySent && <div className="auth-success">Verification email sent again. Check your inbox and spam.</div>}
          {resendVerifyCooldown > 0 && (
            <p className="auth-hint">Please wait before requesting another email to avoid too many requests.</p>
          )}
          {verifyCheckMessage === "checking" && <p className="auth-success">Checking…</p>}
          {verifyCheckMessage === "not_yet" && <p className="auth-error">Not verified yet. Click the link in your email (<span className="verify-check-spam">Check spam</span>), then try again.</p>}
          <button type="button" onClick={handleCheckVerified} className="auth-submit-btn" disabled={verifyCheckMessage === "checking"}>
            {verifyCheckMessage === "checking" ? "Checking…" : "I've verified — continue"}
          </button>
          <button type="button" onClick={handleResendVerifyEmail} className="guest-btn" disabled={resendVerifyCooldown > 0}>
            {resendVerifyCooldown > 0 ? `Resend in ${resendVerifyCooldown}s` : "Resend verification email"}
          </button>
          <div className="verify-email-divider">
            <span>or</span>
          </div>
          <div className="verify-email-alt-actions">
            <button type="button" onClick={handleUseDifferentEmail} className="switch-btn verify-alt-btn">
              Use a different email
            </button>
            <button type="button" onClick={handleGuestFromVerify} className="guest-btn verify-alt-btn">
              Continue as guest
            </button>
          </div>
          <p className="verify-email-alt-hint">Guest mode does not need email verification. Chats stay in this browser only.</p>
        </div>
      </div>
      </>
    );
  }

  // Forgot password (no user logged in)
  if (!user && !isGuest && !authLoading && showForgotPassword) {
    return (
      <>
      <ToastContainer />
      <div className="landing-container">
        <div className="auth-box">
          <h2 className="auth-title">Reset password</h2>
          {forgotPasswordSent ? (
            <>
              <p className="auth-hint">Check <strong>{forgotPasswordEmail}</strong> for a link to reset your password. Then log in with your new password.</p>
              <AuthEmailSpamHint />
              <button type="button" onClick={() => { setShowForgotPassword(false); setForgotPasswordSent(false); setForgotPasswordEmail(""); setForgotPasswordError(""); }} className="auth-submit-btn">Back to login</button>
            </>
          ) : (
            <>
              <p className="auth-hint">Enter your email and we&apos;ll send you a link to reset your password.</p>
              <AuthEmailSpamHint />
              {forgotPasswordError && <div className="auth-error">{forgotPasswordError}</div>}
              <form onSubmit={handleForgotPasswordSubmit} className="auth-form">
                <input type="email" placeholder="Email" className="auth-input" value={forgotPasswordEmail} onChange={e => setForgotPasswordEmail(e.target.value)} required />
                <button type="submit" className="auth-submit-btn">Send reset link</button>
              </form>
              <button type="button" onClick={() => { setShowForgotPassword(false); setForgotPasswordEmail(""); setForgotPasswordError(""); }} className="switch-btn" style={{ marginTop: 12 }}>Back to login</button>
            </>
          )}
        </div>
      </div>
      </>
    );
  }

  // Landing View (login / sign up)
  if (!user && !isGuest && !authLoading) {
    return (
      <>
      <ToastContainer />
      <div className="landing-container">
        {showWelcome ? (
          <div className="welcome-popup">
            <h2 className="welcome-title">
              <span className="welcome-logo-wrap logo-hex-wrap">
                <img src="/logo-hex.png" alt="Oberoi AI" className="app-logo welcome-logo" />
              </span>
            </h2>
            <p className="welcome-text">Hi! Welcome to Oberoi AI.</p>
            <p className="welcome-subtext">Your intelligent companion.</p>
            <div className="welcome-buttons">
              <button onClick={() => setShowWelcome(false)} className="start-button">Start Chatting</button>
              <button onClick={() => { setShowWelcome(false); setShowAuthModal(true); }} className="login-link-btn">Login to Account</button>
            </div>
          </div>
        ) : (
          <div className="auth-box">
            <h2 className="auth-title">{isSignUp ? 'Create Account' : 'Login'}</h2>
            {authError && <div className="auth-error">{authError}</div>}
            <form onSubmit={handleAuth} className="auth-form">
              {isSignUp && (
                <div className="name-inputs">
                  <input placeholder="First Name" className="auth-input" value={firstName} onChange={e => setFirstName(e.target.value)} required />
                  <input placeholder="Last Name" className="auth-input" value={lastName} onChange={e => setLastName(e.target.value)} required />
                </div>
              )}
              <input type="email" placeholder="Email" className="auth-input" value={email} onChange={e => setEmail(e.target.value)} required />
              <input type="password" placeholder="Password" className="auth-input" value={password} onChange={e => setPassword(e.target.value)} required />
              {isSignUp && <input type="password" placeholder="Confirm Password" className="auth-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />}
              <button type="submit" className="auth-submit-btn">{isSignUp ? 'Sign Up' : 'Login'}</button>
            </form>
            {!isSignUp && (
              <button type="button" onClick={() => { setShowForgotPassword(true); setForgotPasswordEmail(email); setForgotPasswordError(""); setAuthError(""); }} className="auth-forgot-link login-forgot-link">Forgot password?</button>
            )}
            <button onClick={handleGuestMode} className="guest-btn">Continue as Guest</button>
            <div className="auth-switch">
              {isSignUp ? 'Already have!' : "No account?"}
              <button type="button" onClick={() => { setIsSignUp(!isSignUp); setAuthError(''); }} className="switch-btn">{isSignUp ? 'Login' : 'Sign Up'}</button>
            </div>
          </div>
        )}
      </div>
      </>
    );
  }

  return (
    <div className="app-container">
      {isMobile && sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}
      <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <button onClick={createNewChat} className="new-chat-btn">+ New Chat</button>
        <div className="chat-search-wrapper">
          <input
            type="text"
            className="chat-search-input"
            placeholder="Search chats..."
            value={chatSearchQuery}
            onChange={(e) => setChatSearchQuery(e.target.value)}
          />
          {chatSearchQuery && (
            <button className="chat-search-clear" onClick={() => setChatSearchQuery('')} aria-label="Clear search">&times;</button>
          )}
        </div>
        <h3 className="sidebar-chats-heading">Chats</h3>
        <div className={`history-list ${isBulkDeleting ? 'history-list-busy' : ''}`}>
          {isBulkDeleting && (
            <div className="history-list-overlay" aria-live="polite">
              <LoadingSpinner size="medium" label="Deleting all chats" />
              <span>Deleting chats…</span>
            </div>
          )}
          {displayChats.length === 0 ? (
            <p className="sidebar-empty">{chatSearchQuery.trim() ? 'No chats match your search.' : 'No chats yet. Start a new one above.'}</p>
          ) : (
            displayChats.map((chat, chatIdx) => (
              <div
                key={chat.id}
                className={`history-item animate-in ${chat.id === currentChatId ? 'active' : ''} ${isTempChat(chat.id) ? 'history-item-temp' : ''} ${deletingChatId === chat.id ? 'history-item-deleting' : ''}`}
                style={{ '--anim-delay': `${Math.min(chatIdx * 0.035, 0.28)}s` }}
                onClick={() => !deletingChatId && !isBulkDeleting && selectChat(chat.id)}
              >
                <span className="history-item-title">{chat.title || 'New Chat'}</span>
                {deletingChatId === chat.id ? (
                  <span className="history-item-loading" aria-hidden="true">
                    <LoadingSpinner size="small" label="Deleting chat" />
                  </span>
                ) : (
                  <button
                    type="button"
                    className="history-item-delete"
                    onClick={(e) => handleDeleteChat(e, chat.id)}
                    title="Delete chat"
                    aria-label="Delete chat"
                    disabled={!!deletingChatId || isBulkDeleting}
                  >
                    ×
                  </button>
                )}
              </div>
            ))
          )}
        </div>
        <div className="sidebar-footer">
          {(user || isGuest) && (
            <div className="usage-counter" title="Estimated usage. Free for you — no payment needed.">
              Estimated Usage {user ? '(till date)' : '(this session)'}: ${(totalUsageUsd || 0).toFixed(4)}
              {(totalUsageUsd || 0) >= USAGE_WARN_USD && (
                <span className="usage-warn"> — high usage this {user ? 'account' : 'session'}</span>
              )}
              <span className="usage-free"> · Free for you</span><span className="usage-testing"> due to testing phase</span>
            </div>
          )}
          {user && userProfile && (
            <div className="user-info-area">
              <div className="user-name">{userProfile.displayName || user.email}</div>
              <div className="user-account-number">Account: {userProfile.accountNumber}</div>
              <div className="user-email">{userProfile.email}</div>
            </div>
          )}
          {user && !userProfile && <div className="user-info-area"><div>{user.displayName || user.email}</div></div>}
          {isGuest && (
            <div className="user-info-area guest-label">
              {isLocalGuestSession()
                ? 'Guest (this device) — not saved to cloud'
                : 'Guest — chats saved in this session only'}
            </div>
          )}
          <div className="sidebar-footer-actions">
            {user && (
              <button
                type="button"
                onClick={() => setShowSettingsModal(true)}
                className="settings-icon-btn"
                title="Settings"
                aria-label="Settings"
              >
                <svg className="settings-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            )}
            <button type="button" onClick={handleLogoutClick} className="logout-btn">{user && !isGuest && !user?.isAnonymous ? 'Logout' : 'Exit Guest'}</button>
          </div>
        </div>
      </div>

      <div className="container main-chat-panel">
        <header className="header">
          <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu">☰</button>
          <div className="header-brand-row">
            <div className="brand logo-hex-wrap">
              <img src="/logo-hex.png" alt="Oberoi AI" className="app-logo" />
            </div>
          </div>
          <div className="controls">
            {currentChatId && (
              <div className="header-conversation-title">{headerChatTitle}</div>
            )}
            {user && (
              <button type="button" onClick={startTemporaryChat} className="header-temp-chat-btn" title="Not saved; gone when you close the tab">
                <span className="header-temp-chat-line1">Temporary</span>
                <span className="header-temp-chat-line2">chat</span>
              </button>
            )}
            <div className="control-group">
              <div className={`custom-select ${messages.length > 0 ? 'locked' : ''}`} onClick={() => messages.length === 0 && setModelMenuOpen(!modelMenuOpen)}>
                <div className="selected-value">{activeModel === 'gpt-4o' ? 'v1' : 'v2'}</div>
                <span className="arrow">▼</span>
                {modelMenuOpen && (
                  <div className="dropdown-menu">
                    <div className="menu-item" onClick={() => { changeModel('gpt-4o'); setModelMenuOpen(false); }}>v1</div>
                    <div className="menu-item" onClick={() => { changeModel('gpt-5.2'); setModelMenuOpen(false); }}>v2</div>
                  </div>
                )}
              </div>
              <div className="model-selector-label">{activeModel === 'gpt-4o' ? 'Faster' : 'Stronger with Web Results'}</div>
            </div>
          </div>
        </header>

        <div className="messages" ref={messagesContainerRef}>
          {displayMessages.length === 0 && <div className="message oberoi animate-in">
            Hello {user?.displayName?.split(' ')[0] || firstName || 'Guest'}!
          </div>}
          {displayMessages.map((msg, i) => {
            const isError = Boolean(msg.isError) || msg.sender === 'system';
            const sender = isError ? 'system' : normalizeSender(msg.sender);
            const isOberoi = !isError && sender === 'Oberoi';
            const isUser = sender === 'You';
            const hasOberoiContent =
              isOberoi &&
              !msg.isStreaming &&
              (!!(msg.text || '').trim() || (msg.attachments || []).length > 0);
            const showOberoiActions = hasOberoiContent;
            const showRegenerate = showOberoiActions && i === lastOberoiDisplayIdx && canRegenerate;
            const showEdit =
              isUser &&
              i === lastUserDisplayIdx &&
              canEditPrompt &&
              (!!(msg.text || '').trim() || (msg.attachments || []).length > 0);
            const hasActions = showOberoiActions || showEdit;
            const actionKey = msg.id != null ? String(msg.id) : `idx-${i}`;
            const actionsVisible = hasActions;
            const webSources = Array.isArray(msg.webSources) ? msg.webSources : [];
            const hasWebSources = webSources.length > 0;
            const webSourcesOpen = openWebSourcesKey === actionKey;
            const isNewMessage = i >= animateMessageFromIndex;
            return (
            <div
              key={msg.id || i}
              className={`message-row${isNewMessage ? ' animate-in' : ''} message-row--${isError ? 'error' : isUser ? 'user' : 'oberoi'}${hasActions ? ' message-row--has-actions' : ''}`}
              style={isNewMessage ? { '--anim-delay': `${Math.min((i - animateMessageFromIndex) * 0.04, 0.2)}s` } : undefined}
            >
              {hasActions && (
                <div
                  className={`message-outside-actions${actionsVisible ? ' is-visible' : ''}`}
                  aria-hidden={!actionsVisible}
                >
                  {showOberoiActions && (
                    <>
                      <button type="button" className="msg-action-btn" onClick={() => copyMessageText(msg.text)}>Copy</button>
                      {showRegenerate && (
                        <button type="button" className="msg-action-btn" onClick={handleRegenerate}>Regenerate</button>
                      )}
                    </>
                  )}
                  {showEdit && (
                    <button type="button" className="msg-action-btn" onClick={handleEditLastPrompt}>Edit</button>
                  )}
                </div>
              )}
              <div className={`message ${isError ? 'error' : isUser ? 'user' : 'oberoi'}`}>
                {renderContent(msg.text, resolveAttachmentsForDisplay(msg.attachments || []), msg.isStreaming)}
              </div>
              {hasWebSources && (
                <div
                  ref={webSourcesOpen ? webSourcesContainerRef : null}
                  className={`web-sources-below-reply${webSourcesOpen ? ' is-open' : ''}${webSourcesAboveKey === actionKey ? ' opens-up' : ''}`}
                >
                  <button
                    type="button"
                    className="web-sources-toggle"
                    onClick={(event) => toggleWebSources(event, actionKey, webSources.length)}
                    aria-expanded={webSourcesOpen}
                    aria-controls={`web-sources-${actionKey}`}
                  >
                    <span className="web-sources-toggle-icon" aria-hidden="true">⌘</span>
                    Websites visited ({webSources.length})
                    <span className="web-sources-chevron" aria-hidden="true">⌄</span>
                  </button>
                  {webSourcesOpen && (
                    <div id={`web-sources-${actionKey}`} className="web-sources-dropdown">
                      {webSources.map((source, sourceIndex) => (
                        <a
                          key={`${source.url}-${sourceIndex}`}
                          href={source.url}
                          target="_blank"
                          rel="noreferrer"
                          className="web-source-link"
                        >
                          <span className="web-source-link-title">{source.title}</span>
                          <span className="web-source-link-url">{source.url}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-bar-wrap">
          <p className="input-disclaimer">{getAppDisclaimer()}</p>
          <div className="input-area">
            <textarea
              ref={inputTextareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask something..."
              rows={1}
              className="chat-textarea"
            />
            <button onClick={() => fileInputRef.current.click()} className="attach-btn" disabled={isLoading || isProcessingFile} title="Images, PDF, Word, Excel, PowerPoint, text">📎</button>
            <input type="file" multiple ref={fileInputRef} style={{ display: 'none' }} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json,.zip,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/zip,application/x-zip-compressed,text/plain,text/csv,application/json" onChange={(e) => handleFileSelect(e, false)} />
            <button onClick={() => cameraInputRef.current.click()} className="camera-btn" disabled={isLoading || isProcessingFile} title="Take photo">📷</button>
            <input type="file" capture="environment" ref={cameraInputRef} style={{ display: 'none' }} accept="image/*" onChange={(e) => handleFileSelect(e, true)} />
            <button className={`send-button ${isLoading ? 'stop' : ''}`} onClick={isLoading ? stopStreaming : sendMessage}>
              {isLoading ? 'Stop' : 'Send'}
            </button>
          </div>
        </div>

        {isProcessingFile && (
          <div className="file-processing-banner" aria-live="polite">Reading {processingFileName || "file"}…</div>
        )}
        {attachedFiles.length > 0 && (
          <div className="attached-preview message-attachments">
            {attachedFiles.map((attach, index) => (
              <AttachmentChip
                key={index}
                attach={attach}
                compact
                onPreviewClick={openAttachmentPreview}
                onRemove={() => removeAttachedFile(index)}
              />
            ))}
          </div>
        )}
      </div>

      <ToastContainer />
      {expandedImage && (
        <div className="expanded-image-modal expanded-image-modal--safe" onClick={() => setExpandedImage(null)}>
          <img src={expandedImage} alt="Expanded" className="expanded-image" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {showSettingsModal && (
        <div className="modal-overlay overlay-animate" onClick={() => !isBulkDeleting && !deletingChatId && setShowSettingsModal(false)}>
          <div className="settings-modal modal-content-animate" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="settings-modal-title">
            <div className="settings-modal-header">
              <h2 id="settings-modal-title" className="settings-modal-title">Settings</h2>
              <button
                type="button"
                className="settings-modal-close-icon"
                onClick={() => setShowSettingsModal(false)}
                aria-label="Close settings"
              >
                ×
              </button>
            </div>

            <div className="settings-modal-body">
              <p className="settings-memory-note">
                Memory keeps useful facts across your saved chats (not temporary chats).
              </p>
              <nav className="settings-menu" aria-label="Settings actions">
                <button
                  type="button"
                  className="settings-item settings-item--warn"
                  onClick={() => { setShowSettingsModal(false); handleClearMemory(); }}
                  disabled={!userMemory?.trim() || !!deletingChatId || isBulkDeleting}
                >
                  Clear memory
                </button>
                <button
                  type="button"
                  className="settings-item settings-item--warn"
                  onClick={() => { setShowSettingsModal(false); handleDeleteAllChats(); }}
                  disabled={savedChatCount === 0 || !!deletingChatId || isBulkDeleting}
                >
                  Delete all chats
                </button>
                <button
                  type="button"
                  className="settings-item"
                  onClick={() => {
                    setShowSettingsModal(false);
                    setShowChangePasswordModal(true);
                    setChangePasswordError("");
                    setChangePasswordCurrent("");
                    setChangePasswordNew("");
                    setChangePasswordConfirm("");
                    setChangePasswordResetSent(false);
                  }}
                >
                  Change password
                </button>
                <button
                  type="button"
                  className="settings-item settings-item--danger"
                  onClick={() => { setShowSettingsModal(false); handleDeleteAccount(); }}
                >
                  Delete account
                </button>
              </nav>
            </div>

            <div className="settings-modal-footer">
              <p className="settings-modal-disclaimer">{getAppDisclaimer()}</p>
              <div className="settings-brand-block">
                <p className="settings-brand-title">
                  {APP_NAME} <span className="settings-brand-ver">V - {APP_VERSION}</span>
                </p>
                <p className="settings-brand-credit">By {APP_AUTHOR}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showChangePasswordModal && (
        <div className="modal-overlay overlay-animate" onClick={() => !changePasswordLoading && !changePasswordResetSent && setShowChangePasswordModal(false)}>
          <div className="change-password-modal modal-content-animate" onClick={(e) => e.stopPropagation()}>
            <h2 className="change-password-title">Change Password</h2>
            {changePasswordResetSent ? (
              <>
                <p className="change-password-message">We sent a reset link to your email.</p>
                <AuthEmailSpamHint />
                <button type="button" onClick={() => { setShowChangePasswordModal(false); setChangePasswordResetSent(false); }} className="auth-submit-btn">Close</button>
              </>
            ) : (
              <>
                {!isCustomAuthEmailEnabled() && (
                  <p className="change-password-hint">Reset links are sent by Firebase — check spam if needed.</p>
                )}
                {isCustomAuthEmailEnabled() && (
                  <p className="change-password-hint">
                    Emails from us may land in spam. Look for <strong>{getAuthEmailFromLabel()}</strong>.
                  </p>
                )}
                {changePasswordError && <div className="auth-error">{changePasswordError}</div>}
                <form onSubmit={handleChangePassword} className="auth-form">
                  <input type="password" placeholder="Current password" className="auth-input" value={changePasswordCurrent} onChange={e => setChangePasswordCurrent(e.target.value)} required autoComplete="current-password" />
                  <button type="button" onClick={async () => { try { await sendOberoiPasswordResetEmail(user?.email); setChangePasswordResetSent(true); setChangePasswordError(""); } catch (e) { setChangePasswordError(e.message?.replace("Firebase:", "").trim() || "Failed to send."); } }} className="change-password-forgot-link">Forgot password?</button>
                  <input type="password" placeholder="New password (min 6 characters)" className="auth-input" value={changePasswordNew} onChange={e => setChangePasswordNew(e.target.value)} required minLength={6} autoComplete="new-password" />
                  <input type="password" placeholder="Confirm new password" className="auth-input" value={changePasswordConfirm} onChange={e => setChangePasswordConfirm(e.target.value)} required minLength={6} autoComplete="new-password" />
                  <button type="submit" className="auth-submit-btn" disabled={changePasswordLoading}>{changePasswordLoading ? "Updating…" : "Update Password"}</button>
                </form>
                <button type="button" onClick={() => !changePasswordLoading && setShowChangePasswordModal(false)} className="confirm-modal-cancel change-password-cancel">Cancel</button>
              </>
            )}
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="modal-overlay overlay-animate" onClick={() => !confirmModal.loading && setConfirmModal(prev => ({ ...prev, open: false }))}>
          <div className={`confirm-modal-box modal-content-animate ${confirmModal.loading ? 'confirm-modal-box--loading' : ''}`} onClick={(e) => e.stopPropagation()}>
            {confirmModal.loading && (
              <div className="confirm-modal-loading" aria-live="polite">
                <LoadingSpinner size="large" label={confirmModal.loadingLabel} />
                <p>{confirmModal.loadingLabel}</p>
              </div>
            )}
            <h2 className="confirm-modal-title">{confirmModal.title}</h2>
            <p className="confirm-modal-message">{confirmModal.message}</p>
            <div className="confirm-modal-actions">
              <button
                type="button"
                onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                className="confirm-modal-cancel"
                disabled={confirmModal.loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmModalAction}
                className={confirmModal.danger ? 'confirm-modal-confirm danger' : 'confirm-modal-confirm'}
                disabled={confirmModal.loading}
              >
                {confirmModal.loading ? (
                  <span className="confirm-modal-confirm-inner">
                    <LoadingSpinner size="small" label={confirmModal.loadingLabel} />
                    {confirmModal.loadingLabel}
                  </span>
                ) : (
                  confirmModal.confirmLabel
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;